<template>
    <div ref="wrapperElement" class="map flex flex-column" :class="{rounded: rounded}">
        <div class="flex-grow-1 h-auto" ref="mapElement" :style="{cursor: cursor, zIndex: 2}">

            <div v-if="props.controllable && !props.hideControls" class="absolute z-50 top-0 right-0 m-10">
                <div class="flex flex-column">
                   <btn-group v-if="props.locateButton" vertical>
					   <btn small @click="setCenterVisitor" class="w-36 min-w-36 h-36 min-h-36 p-0" :title="$t('action.show_me_on_map')">
						   <cdn-image v-if="$auth.user && $auth.user.avatar_thumbnail" rounded :src="$auth.user.avatar_thumbnail" :width="36"/>
						   <Icon v-else :icon="icons.user"/>
					   </btn>
                   </btn-group>

                    <btn-group vertical>
						<btn small icon @click="switchTileLayer" class="w-36 min-w-36 h-36 min-h-36" :title="$t('map.actions.switch_tile_layer')">
							<Icon :icon="icons.map"/>
						</btn>

						<btn v-if="enableLargeScreen && isFullScreen" small icon @click="isFullScreen = false" class="w-36 min-w-36 h-36 min-h-36" :title="$t('map.actions.stop_large_screen_mode')">
							<Icon :icon="icons.minimize_screen"/>
						</btn>
						<btn v-else-if="enableLargeScreen" small icon @click="isFullScreen = true" class="w-36 min-w-36 h-36 min-h-36" :title="$t('map.actions.start_large_screen_mode')">
							<Icon :icon="icons.maximize_screen"/>
						</btn>
                    </btn-group>

                    <slot name="controls"/>

                </div>
            </div>

            <map-marker v-if="visitorLocation" :lat-lng="visitorLocation"
                        :content="{content: $auth.user ? $auth.user.display_name : $t('text.own_location')}"
                        :icon="!$auth.user ? icons.user : null"
                        :width="34"
                        :height="34"
                        :image="$auth.user ? $image.format($auth.user.avatar_thumbnail, {width: 34 * 2, height: 34 * 2}) : null"/>

            <template v-if="isMapLoaded">
				<slot/>
			</template>

        </div>
        <slot name="footer"/>
    </div>
</template>

<script setup lang="ts">
    import {
        mdiAccount,
        mdiMonitorScreenshot,
        mdiClose,
        mdiMapOutline,
    } from "@mdi/js";
	import {formatLatLng} from "@spoferan/spoferan-ts-core";
	import type {LatLng} from "@spoferan/spoferan-ts-core";
    import {GeolocationService} from "../../services";
	import {FullScreenComponentEmits, type FullScreenComponentProps, useFullScreenComponent} from "../../composables/useFullScreenComponent";
	import type {Ref} from 'vue';
	import {watch, provide, onMounted, onBeforeUnmount, nextTick, ref} from 'vue';
	import {mapInjection} from "../../types/keys";

	// The tile layers to enable for selection
	const tileLayers = ['https://{s}.tile.openstreetmap.de/tiles/osmde/{z}/{x}/{y}.png', 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}']

	const icons = {
		map: mdiMapOutline,
		user: mdiAccount,
		maximize_screen: mdiMonitorScreenshot,
		minimize_screen: mdiClose,
	}

	const props = withDefaults(defineProps<{
		center?: [number, number] | LatLng,

		// States if the geolocation of the visitor shall be used as the center of the map on initialization
		centerVisitor?: boolean,

		// Whether a button to locate the user's location shall be displayed
		locateButton?: boolean,

		// The zoom to use when the map gets initialized
		zoom?: number,

		// The opacity of the map's tiles
		tileOpacity?: number,

		// The min zoom of the map
		minZoom?: number,

		// The bounds geolocation of the map
		bounds?: object,

		// States if the visitor can enable the large screen mode of the map
		enableLargeScreen?: boolean,

		// Whether to hide the controls on the map
		hideControls?: boolean,

		// States if controls shall be shown on the map
		controllable?: boolean,

		// States if a border radius shall be applied on the map
		rounded?: boolean,

		// An object or array of additional object containing either a 'lat' and 'lng' prop or a 'latlngs' which is zoomed to on initial map load
		focusElements?: {lat: number, lng: number} | {latlngs: {lat: number, lng: number}[]} | {lat: number, lng: number}[] | {latlngs: {lat: number, lng: number}[]}[],

		// The cursor style to use on the map tiles
		cursor?: string,
	} & FullScreenComponentProps>(), {
		minZoom: 4,
		enableLargeScreen: true,
		controllable: true,
		cursor: 'grab'
	});

	const emit = defineEmits([
		...FullScreenComponentEmits,
		'ready',
		'click', // Emitted when the user clicks on the map
		'move', // Emitted when the user moves the map
		'visitorlocationchanged',
		'update:center',
		'update:zoom',
		'update:bounds'
	]);

	const mapElement: Ref<HTMLElement|null> = ref(null);
	const wrapperElement: Ref<HTMLElement|null> = ref(null);

	// The underlying Leaflet map instance
	let mapObject = null;
	let mapTile = null;

	const isMapLoaded = ref(false);

	const mapZoom = ref(props.zoom ?? 7);
	const mapCenter = ref(props.center ?? [51.165691, 10.451526]);
	const mapBounds = ref(props.bounds);

	// The tile layer to currently show on the map
	const tileLayer = ref(tileLayers[0]);

	// The location of the visitor. Will be calculated when the visitor accepted the permission to locate his position.
	const visitorLocation = ref(null);

	const {isFullScreen} = useFullScreenComponent(wrapperElement, props, emit);
	watch(isFullScreen, () => {
		invalidate();
	});

	watch(visitorLocation, (newValue) => {
		emit('visitorlocationchanged', newValue);
	});

	watch(() => props.center, (newValue, oldValue) => {
		mapCenter.value = newValue;

		// We need to check if the lat or lng changed as otherwise this would end in an infinite loop as newValue and oldValue might be different objects
		const oldLatLng = oldValue ? formatLatLng(oldValue, 5) : null;
		const newLatLng = newValue ? formatLatLng(newValue, 5) : null;
		if (newLatLng && (!oldLatLng || (oldLatLng.lat !== newLatLng.lat && oldLatLng.lng !== newLatLng.lng))) {
			flyTo(newLatLng);
		}
	});
	watch(mapCenter, (newValue) => {
		emit('update:center', newValue)
	});

	watch(() => props.zoom, (newValue) => {
		mapZoom.value = newValue;
	});
	watch(mapZoom, (newValue) => {
		emit('update:zoom', newValue)
	});

	watch(() => props.bounds, (newValue) => {
		mapBounds.value = newValue;
	});
	watch(mapBounds, (newValue) => {
		emit('update:bounds', newValue)
	});

	watch(() => props.focusElements, () => {
		zoomToFocusElements();
	});

	/**
	 * Tries to find the visitor's geolocation and centers the map to that location.
	 */
	function setCenterVisitor() {
		if (visitorLocation.value) {
			flyTo(visitorLocation.value, true)
		} else {
			GeolocationService.locate((latLng) => {
				visitorLocation.value = latLng;
				flyTo(visitorLocation.value, true)
			})
		}
	}

	/**
	 * Centers the map to the specified geolocation using a smooth animation.
	 */
	function flyTo(latLng, focusMapElement = false, zoom = null) {
		latLng = formatLatLng(latLng);

		// Center and zoom need to be set first
		if (mapZoom.value && latLng && latLng.lat && latLng.lng && mapObject) {
			zoom = zoom ?? mapZoom.value;

			mapObject.flyTo(latLng, zoom);
			if (focusMapElement) {
				focus();
			}
		}
	}

	/**
	 * Scrolls to the window position of the map.
	 */
	function focus() {
		// TODO: Check if required; currently causes an ugly scroll on events and clubs index page after hydrating
		// scrollToSelector(this.$el);
	}

	function switchTileLayer() {
		let newTileLayerIndex = tileLayers.indexOf(tileLayer.value) + 1;
		newTileLayerIndex = newTileLayerIndex >= tileLayers.length ? 0 : newTileLayerIndex;
		tileLayer.value = tileLayers[newTileLayerIndex];
		if (mapTile) {
			mapTile.setUrl(tileLayer.value);
		}
	}

	/**
	 * Proceeds any action to re-render the map object.
	 * Fix for partial gray scale
	 */
	function invalidate() {
		if (mapObject) {
			mapObject.invalidateSize();
		}
	}

	function zoomToFocusElements() {
		if (mapObject) {
			const latLngs = [];

			const focusElements = Array.isArray(props.focusElements) ? props.focusElements : [props.focusElements];
			if (focusElements && focusElements.length) {
				focusElements.forEach(element => {
					if (element) {
						if (element.latlngs && element.latlngs.length) {
							latLngs.push(...element.latlngs)
						} else if (element.lat && element.lng) {
							latLngs.push(element)
						}
					}
				});
			}

			if (latLngs.length) {
				mapObject.fitBounds(latLngs);
			}
		}
	}

	function addElement(element) {
		if (mapObject) {
			element.addTo(mapObject);
		}
	}

	function refreshBounds() {
		if (mapObject) {
			mapBounds.value = mapObject.getBounds();
			mapCenter.value = mapObject.getCenter();
		}
	}

	onMounted(() => {
		// nextTick to wait for mapElement to be rendered
		nextTick(async () => {
			const {map: leafletMap, tileLayer: leafletTileLayer} = await import('leaflet/dist/leaflet-src.esm');

			mapObject = leafletMap(mapElement.value, {
				center: mapCenter.value,
				zoom: mapZoom.value,
				dragging: props.controllable,
				boxZoom: props.controllable,
				zoomSnap: 0.5,
				zoomDelta: 0.5,
				keyboard: props.controllable,
				scrollWheelZoom: props.controllable,
				zoomControl: false, // Disable zoom controls as we add them manually to define the position,
			});

			mapTile = leafletTileLayer(tileLayer.value, {
				maxZoom: 18,
				minZoom: props.minZoom,
				opacity: props.tileOpacity,
				attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>-Mitwirkende'
			});
			addElement(mapTile);

			refreshBounds();

			if (props.centerVisitor) {
				setCenterVisitor();
			} else {
				zoomToFocusElements();
			}

			isMapLoaded.value = true;
			emit('ready', mapObject);

			mapObject.on('click', (event) => {
				emit('click', event);
			});

			mapObject.on('move', (event) => {
				refreshBounds();

				if ((window.TouchEvent && event.originalEvent instanceof TouchEvent) || (window.MouseEvent && event.originalEvent instanceof MouseEvent)) {
					emit('move', event);
				}
			});

			mapObject.on('zoomend', () => {
				mapZoom.value = mapObject.getZoom();
			});
		})

	});

	onBeforeUnmount(() => {
		if (mapObject) {
			mapObject.remove();
		}
	})

	provide(mapInjection, {
		mapObject,
		addElement
	});

	defineExpose({
		flyTo,
	})

</script>
<style>
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
/*********************
  Inputs
**********************/
@import 'leaflet/dist/leaflet.css';
.map {
  height: 100%;
  width: 100%;
  overflow: hidden;
}
.map .leaflet-map-pane {
  z-index: 49;
}
.map .leaflet-map-pane svg {
  z-index: 1;
}
.map .leaflet-container {
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  z-index: 1;
}
.map .leaflet-container a {
  color: #008200;
}
.map .leaflet-container a.leaflet-popup-close-button {
  color: #7D8484;
}
.map .leaflet-container .leaflet-popup-content-wrapper {
  padding: 0;
}
.map .leaflet-container .leaflet-popup-content-wrapper .leaflet-popup-content {
  margin: 0;
  line-height: 1.2;
}
.map .leaflet-container .leaflet-popup-content-wrapper .leaflet-popup-content p {
  margin: 0 0 16px;
  line-height: 1.2;
}
</style>